<template>
  <div
    ref="root"
    class="drp"
    :class="{ 'drp--open': isOpen, 'drp--disabled': disabled }"
  >
    <button
      type="button"
      class="drp__trigger form-select"
      :disabled="disabled"
      @click="toggleDropdown"
    >
      <span
        class="drp__trigger-val"
        :class="{ 'drp__trigger-val--empty': !displayValue }"
      >
        {{ displayValue || placeholder }}
      </span>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    </button>

    <Transition name="fade-slide">
      <div v-if="isOpen" class="drp__dropdown card" @mousedown.stop>
        <div class="drp-title">Пресеты</div>

        <div v-if="presets.length" class="drp__presets">
          <button
            v-for="preset in presets"
            :key="preset.key"
            type="button"
            class="drp__preset"
            :class="{
              'drp__preset--active': activePreset === preset.key,
            }"
            @click="applyPreset(preset)"
          >
            {{ preset.label }}
          </button>
        </div>

        <div class="drp-title">Период</div>

        <div class="drp__inputs">
          <div class="drp__input-group">
            <span class="drp__input-label">Начало</span>
            <input
              :value="inputFrom"
              type="text"
              inputmode="numeric"
              class="form-select drp__input"
              :class="{ 'drp__input--filled': draftFrom }"
              placeholder="ДД.ММ.ГГГГ"
              @input="onInput('from', $event.target.value)"
              @blur="syncInputs"
            />
          </div>
          <div class="drp__input-sep-wrap">
            <span class="drp__input-sep">—</span>
          </div>
          <div class="drp__input-group">
            <span class="drp__input-label">Конец</span>
            <input
              :value="inputTo"
              type="text"
              inputmode="numeric"
              class="form-select drp__input"
              :class="{ 'drp__input--filled': draftTo }"
              placeholder="ДД.ММ.ГГГГ"
              @input="onInput('to', $event.target.value)"
              @blur="syncInputs"
            />
          </div>
          <button
            v-if="draftFrom || draftTo"
            type="button"
            class="drp__clear"
            title="Сбросить"
            @click="clearRange"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div class="drp-title">Календарь</div>

        <!-- Навигация по месяцам -->
        <div class="drp__nav">
          <button
            type="button"
            class="drp__nav-btn"
            @click="shiftMonth(-1)"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div class="drp__nav-selects">
            <BaseSelect
              :model-value="viewMonth"
              :options="monthOptions"
              :clearable="false"
              @update:model-value="onMonthChange"
              @click.stop
            />
            <BaseSelect
              :model-value="viewYear"
              :options="yearOptions"
              :clearable="false"
              @update:model-value="onYearChange"
              @click.stop
            />
          </div>

          <button
            type="button"
            class="drp__nav-btn"
            :disabled="!canGoForward"
            @click="shiftMonth(1)"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        <div class="drp__month">
          <div class="drp__weekdays">
            <span v-for="d in WEEKDAYS" :key="d" class="drp__weekday">
              {{ d }}
            </span>
          </div>
          <div class="drp__days">
            <span
              v-for="n in monthData.startOffset"
              :key="'e' + n"
              class="drp__day drp__day--empty"
            />
            <button
              v-for="day in monthData.days"
              :key="day.date"
              type="button"
              class="drp__day"
              :class="getDayClasses(day.date)"
              :disabled="isDayDisabled(day.date)"
              @click="onDayClick(day.date)"
              @mouseenter="onDayMouseEnter(day.date)"
              @mouseleave="hoverDate = null"
            >
              {{ day.dayNum }}
            </button>
          </div>
        </div>

        <!-- Футер -->
        <div class="drp__footer">
          <base-primary-button
            type="button"
            class="drp__btn"
            :disabled="!canApply"
            @click="confirm"
          >
            Применить
          </base-primary-button>
          <base-button type="button" class="drp__btn" @click="cancel">
            Отмена
          </base-button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

export default {
  name: "BaseDateRangePicker",

  props: {
    modelValue: {
      type: Object,
      default: () => ({ from: "", to: "" }),
    },
    placeholder: {
      type: String,
      default: "Выберите период",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    presets: {
      type: Array,
      default: () => [],
    },
    maxRangeDays: {
      type: Number,
      default: null,
    },
    maxDate: {
      type: String,
      default: null,
    },
  },

  emits: ["update:modelValue", "change"],

  data() {
    const now = new Date();
    return {
      WEEKDAYS,
      isOpen: false,
      viewMonth: now.getMonth(),
      viewYear: now.getFullYear(),
      draftFrom: this.modelValue?.from || "",
      draftTo: this.modelValue?.to || "",
      inputFrom: this.modelValue?.from
        ? this.formatDisplay(this.modelValue.from)
        : "",
      inputTo: this.modelValue?.to
        ? this.formatDisplay(this.modelValue.to)
        : "",
      hoverDate: null,
      activePreset: null,
    };
  },

  computed: {
    todayStr() {
      return this.toISO(new Date());
    },

    effectiveMaxDate() {
      return this.maxDate || this.todayStr;
    },

    displayValue() {
      const { from, to } = this.modelValue || {};
      if (!from || !to) return "";
      return `${this.formatDisplay(from)} — ${this.formatDisplay(
        to
      )}`;
    },

    allowedMax() {
      if (!this.maxRangeDays || !this.draftFrom || this.draftTo)
        return null;
      const d = new Date(this.draftFrom);
      d.setDate(d.getDate() + this.maxRangeDays - 1);
      const raw = this.toISO(d);
      return raw > this.effectiveMaxDate
        ? this.effectiveMaxDate
        : raw;
    },

    allowedMin() {
      if (!this.maxRangeDays || !this.draftFrom || this.draftTo)
        return null;
      const d = new Date(this.draftFrom);
      d.setDate(d.getDate() - (this.maxRangeDays - 1));
      return this.toISO(d);
    },

    monthData() {
      const { viewYear: year, viewMonth: month } = this;
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      let startOffset = new Date(year, month, 1).getDay();
      startOffset = startOffset === 0 ? 6 : startOffset - 1;

      const days = [];
      for (let d = 1; d <= daysInMonth; d++) {
        days.push({
          date: this.toISOParts(year, month + 1, d),
          dayNum: d,
        });
      }

      return { startOffset, days };
    },

    monthOptions() {
      return MONTH_NAMES.map((label, idx) => ({ value: idx, label }));
    },

    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 1970; y--) {
        years.push({ value: y, label: String(y) });
      }
      return years;
    },

    canGoForward() {
      const max = new Date(this.effectiveMaxDate);
      return !(
        this.viewYear === max.getFullYear() &&
        this.viewMonth === max.getMonth()
      );
    },

    canApply() {
      return !!(this.draftFrom && this.draftTo);
    },
  },

  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        this.draftFrom = val?.from || "";
        this.draftTo = val?.to || "";
        this.syncInputs();
        this.syncPreset();
        const pivot = val?.to || val?.from;
        if (pivot) {
          const d = new Date(pivot);
          this.viewMonth = d.getMonth();
          this.viewYear = d.getFullYear();
        }
      },
    },
  },

  methods: {
    toggleDropdown() {
      if (this.disabled) return;
      if (!this.isOpen) this.resetDraft();
      this.isOpen = !this.isOpen;
    },

    onOutsideClick(e) {
      if (this.isOpen && !this.$refs.root?.contains(e.target))
        this.cancel();
    },

    resetDraft() {
      this.draftFrom = this.modelValue?.from || "";
      this.draftTo = this.modelValue?.to || "";
      this.hoverDate = null;
      this.syncInputs();
      this.syncPreset();
      const pivot = this.draftTo || this.draftFrom || this.todayStr;
      const d = new Date(pivot);
      this.viewMonth = d.getMonth();
      this.viewYear = d.getFullYear();
    },

    cancel() {
      this.resetDraft();
      this.isOpen = false;
    },

    confirm() {
      if (!this.canApply) return;
      const payload = { from: this.draftFrom, to: this.draftTo };
      this.$emit("update:modelValue", payload);
      this.$emit("change", payload);
      this.isOpen = false;
    },

    clearRange() {
      this.draftFrom = "";
      this.draftTo = "";
      this.inputFrom = "";
      this.inputTo = "";
      this.hoverDate = null;
      this.activePreset = null;
    },

    onDayClick(date) {
      if (this.isDayDisabled(date)) return;
      this.activePreset = null;

      if (!this.draftFrom || (this.draftFrom && this.draftTo)) {
        this.draftFrom = date;
        this.draftTo = "";
        this.hoverDate = null;
        this.syncInputs();
        return;
      }

      let [from, to] =
        date < this.draftFrom
          ? [date, this.draftFrom]
          : [this.draftFrom, date];

      if (this.maxRangeDays) {
        const days = (new Date(to) - new Date(from)) / 86400000 + 1;
        if (days > this.maxRangeDays) return;
      }

      this.draftFrom = from;
      this.draftTo = to;
      this.hoverDate = null;
      this.syncInputs();
      this.syncPreset();
    },

    onDayMouseEnter(date) {
      if (this.isDayDisabled(date)) return;
      if (this.draftFrom && !this.draftTo) {
        this.hoverDate = date;
      } else {
        this.hoverDate = null;
      }
    },

    getDayClasses(date) {
      const cls = [];

      if (this.isDayDisabled(date)) {
        cls.push("drp__day--disabled");
        return cls;
      }

      if (date === this.todayStr) cls.push("drp__day--today");

      const effectiveEnd =
        this.draftTo ||
        (this.hoverDate && this.draftFrom ? this.hoverDate : null);

      if (this.draftFrom && effectiveEnd) {
        const [a, b] =
          this.draftFrom <= effectiveEnd
            ? [this.draftFrom, effectiveEnd]
            : [effectiveEnd, this.draftFrom];

        if (date === a && date === b) cls.push("drp__day--single");
        else if (date === a) cls.push("drp__day--range-start");
        else if (date === b) cls.push("drp__day--range-end");
        else if (date > a && date < b) cls.push("drp__day--in-range");
      } else if (date === this.draftFrom) {
        cls.push("drp__day--range-start");
      }

      return cls;
    },

    isDayDisabled(date) {
      if (date > this.effectiveMaxDate) return true;
      if (this.maxRangeDays && this.draftFrom && !this.draftTo) {
        if (this.allowedMin && date < this.allowedMin) return true;
        if (this.allowedMax && date > this.allowedMax) return true;
      }
      return false;
    },

    onMonthChange(val) {
      this.viewMonth = Number(val);
      this.clampView();
    },

    onYearChange(val) {
      this.viewYear = Number(val);
      this.clampView();
    },

    shiftMonth(dir) {
      let m = this.viewMonth + dir;
      let y = this.viewYear;
      if (m < 0) {
        m = 11;
        y--;
      }
      if (m > 11) {
        m = 0;
        y++;
      }
      this.viewMonth = m;
      this.viewYear = y;
      this.clampView();
    },

    clampView() {
      const max = new Date(this.effectiveMaxDate);
      if (
        this.viewYear > max.getFullYear() ||
        (this.viewYear === max.getFullYear() &&
          this.viewMonth > max.getMonth())
      ) {
        this.viewYear = max.getFullYear();
        this.viewMonth = max.getMonth();
      }
    },

    applyPreset(preset) {
      const range = preset.getRange?.({
        today: this.todayStr,
        toDateStr: this.toISOParts,
      });
      if (!range?.from || !range?.to) return;
      this.draftFrom = range.from;
      this.draftTo = range.to;
      this.activePreset = preset.key;
      this.syncInputs();
      const d = new Date(range.to);
      this.viewMonth = d.getMonth();
      this.viewYear = d.getFullYear();
    },

    syncPreset() {
      if (!this.draftFrom || !this.draftTo) {
        this.activePreset = null;
        return;
      }
      const match = this.presets.find((p) => {
        const r = p.getRange?.({
          today: this.todayStr,
          toDateStr: this.toISOParts,
        });
        return r?.from === this.draftFrom && r?.to === this.draftTo;
      });
      this.activePreset = match ? match.key : null;
    },

    syncInputs() {
      this.inputFrom = this.draftFrom
        ? this.formatDisplay(this.draftFrom)
        : "";
      this.inputTo = this.draftTo
        ? this.formatDisplay(this.draftTo)
        : "";
    },

    onInput(field, raw) {
      const digits = raw.replace(/\D/g, "").slice(0, 8);
      const parts = [
        digits.slice(0, 2),
        digits.slice(2, 4),
        digits.slice(4, 8),
      ].filter(Boolean);
      const formatted = parts.join(".");

      if (field === "from") this.inputFrom = formatted;
      else this.inputTo = formatted;

      if (!/^\d{2}\.\d{2}\.\d{4}$/.test(formatted)) return;
      const iso = this.parseDisplay(formatted);
      if (!iso || iso > this.effectiveMaxDate) return;

      if (field === "from") {
        this.draftFrom = iso;
        if (this.draftTo && this.draftTo < iso) {
          this.draftTo = iso;
          this.inputTo = formatted;
        }
      } else {
        this.draftTo = iso;
        if (this.draftFrom && iso < this.draftFrom) {
          this.draftFrom = iso;
          this.inputFrom = formatted;
        }
      }

      const pivot = new Date(
        field === "from" ? this.draftFrom : this.draftTo
      );
      this.viewMonth = pivot.getMonth();
      this.viewYear = pivot.getFullYear();

      this.activePreset = null;
      this.syncPreset();
    },

    toISO(date) {
      return this.toISOParts(
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate()
      );
    },

    toISOParts(y, m, d) {
      return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(
        2,
        "0"
      )}`;
    },

    formatDisplay(iso) {
      if (!iso) return "";
      const [y, m, d] = iso.split("-");
      return `${d}.${m}.${y}`;
    },

    parseDisplay(str) {
      const [d, m, y] = str.split(".").map(Number);
      const date = new Date(y, m - 1, d);
      if (
        date.getFullYear() !== y ||
        date.getMonth() !== m - 1 ||
        date.getDate() !== d
      )
        return null;
      return this.toISOParts(y, m, d);
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.onOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this.onOutsideClick);
  },
};
</script>

<style scoped>
.drp {
  position: relative;
  width: 100%;
}

.drp__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  cursor: pointer;
  background: #fff !important;
  border: 1px solid #999 !important;
  border-radius: 8px !important;
  padding: 8.5px 16px;
}

.drp__trigger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.drp__trigger svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #999;
}

.drp__trigger-val {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-dark);
  line-height: 1;
}

.drp__trigger-val--empty {
  color: #999;
  font-weight: 400;
  opacity: 0.5;
}

.drp__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 200;
  width: 350px;
  padding: 14px;
  gap: var(--base-in-indent);
}

.drp__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.drp__preset {
  padding: 5px 8px;
  border: 1px solid var(--color-grey);
  border-radius: 20px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #555;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

.drp__preset:hover {
  background: var(--color-grey);
}

.drp__preset--active {
  background: var(--color-green);
  border-color: var(--color-green);
  color: #fff;
}

.drp__preset--active:hover {
  background: var(--color-green-hover);
}

.drp__inputs {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.drp__input-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.drp__input-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #aaa;
  line-height: 1;
}

.drp__input {
  font-size: 16px;
  padding: 10px 12px;
  transition: border-color 0.2s ease;
}

.drp__input--filled {
  border-color: var(--color-green);
}

.drp__input-sep-wrap {
  display: flex;
  align-items: center;
  padding-bottom: 9px;
}

.drp__input-sep {
  color: #ccc;
  font-size: 16px;
  flex-shrink: 0;
}

.drp__clear {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--color-grey);
  border-radius: 6px;
  color: #bbb;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
  margin-bottom: 1px;
  cursor: pointer;
}

.drp__clear:hover {
  background: #ffe0e0;
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.drp__clear svg {
  width: 12px;
  height: 12px;
}

.drp__nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drp__nav-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-grey);
  border-radius: 6px;
  color: #666;
  flex-shrink: 0;
  cursor: pointer;
}

.drp__nav-btn:hover:not(:disabled) {
  background: var(--color-grey);
  color: var(--color-dark);
}

.drp__nav-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.drp__nav-btn svg {
  width: 16px;
  height: 16px;
}

.drp__nav-selects {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 96px;
  gap: 6px;
}

.drp__month {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.drp__weekdays,
.drp__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}

.drp__weekday {
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-align: center;
  padding: 2px 0;
}

.drp__day {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  background: #f5f5f5;
  border: none;
  color: var(--color-dark);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  line-height: 1;
}

.drp__day--empty {
  background: transparent;
  pointer-events: none;
}

.drp__day--disabled {
  opacity: 0.3;
  cursor: default;
}

.drp__day--today {
  box-shadow: inset 0 0 0 1.5px var(--color-blue);
}

.drp__day:not(.drp__day--disabled):not(.drp__day--empty):hover {
  outline: 2px solid var(--color-blue);
  outline-offset: -1px;
  z-index: 2;
}

.drp__day--in-range::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 0;
  background: rgba(76, 110, 245, 0.22);
  pointer-events: none;
}

.drp__day--range-start,
.drp__day--range-end {
  outline: 2.5px solid var(--color-blue);
  outline-offset: -1px;
  z-index: 1;
}

.drp__day--range-start {
  border-radius: 5px 0 0 5px;
}

.drp__day--range-end {
  border-radius: 0 5px 5px 0;
}

.drp__day--range-start::after,
.drp__day--range-end::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: rgba(76, 110, 245, 0.32);
  pointer-events: none;
}

.drp__day--single {
  outline: 3px solid var(--color-blue);
  outline-offset: -1px;
  border-radius: 5px;
  z-index: 2;
  box-shadow: 0 0 0 4px rgba(76, 110, 245, 0.18);
}

.drp__day--single::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 5px;
  background: rgba(76, 110, 245, 0.35);
  pointer-events: none;
}

.drp__footer {
  display: flex;
  gap: 15px;
  padding-top: 4px;
}

.drp__btn {
  flex: 1;
  min-height: 36px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: "Roboto", sans-serif;
}

.drp__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

@media screen and (max-width: 767px) {
  .drp__dropdown {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    overflow-y: auto;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    padding: 16px 14px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }

  .drp__preset {
    font-size: 14px;
    padding: 10px 16px;
  }

  .drp__footer {
    margin-top: auto;
  }

  .drp__day {
    aspect-ratio: unset;
    min-height: 38px;
  }
}
</style>
